<template>
	<DetailsToggle ref="detailsElement" title="Hulp nodig bij het bepalen van de risicoklasse?">
		<div class="fine-and-kinney">
			<p class="risk-description">
				Voor het bepalen van de ernst van een risico kan je gebruik maken van de Fine en Kinney rekenmethode.
			</p>

			<div class="form-wrapper">
				<FormElement
						v-for="item in formSchema"
						:key="item.key"
						:model-value="getModelValue(item.key)"
						:item="item"
						hide-optional-label
						@update:model-value="updateModelValue(item.key, $event)"
				/>
			</div>

			<div class="suggested-risk-level">
				<h4>Voorgestelde risicoklasse</h4>

				<RiskLevelBadge
						v-if="riskLevel"
						:risk="{level: riskLevel}"
						:show-label="false"
				/>
				<p v-else id="risk-level">{{ riskLevelText }}</p>

				<ButtonBar no-spacing-bottom>
					<BaseButton
							v-if="showApplyButton"
							data-testid="apply-suggested-risklevel"
							text="Voorgestelde risicoklasse overnemen"
							btn-class="secondary"
							@click="applyRiskLevel"
					/>
				</ButtonBar>
			</div>
		</div>
	</DetailsToggle>
</template>

<script setup lang="ts">
	import {computed, ref, watch} from 'vue';
	import DetailsToggle from '~/components/generic/DetailsToggle.vue';
	import FormElement from '~/components/forms/FormElement.vue';
	import BaseButton from '~/components/generic/buttons/BaseButton.vue';
	import {getRiskLevelText, getRiskScoreLevel} from '~/lib/riskHelper';
	import type {Risk} from '~/lib/types/riskTypes';
	import ButtonBar from '~/components/generic/buttons/ButtonBar.vue';
	import RiskLevelBadge from '~/components/risks/RiskLevelBadge.vue';
	import {useFineAndKinney} from '~/composables/useFineAndKinney';

	const detailsElement = ref<InstanceType<typeof DetailsToggle> | null>(null);

	const {formFields, formSchema} = useFineAndKinney();

	const riskLevel = computed(() => {
		const {exposure, effect, probability} = formFields.value;
		const exposureNum = parseFloat(exposure);
		const effectNum = parseFloat(effect);
		const probabilityNum = parseFloat(probability);

		return getRiskScoreLevel(exposureNum, effectNum, probabilityNum);
	});

	const riskLevelText = computed(() => {
		return riskLevel.value !== null ? getRiskLevelText(riskLevel.value) : 'Selecteer eerst bovenstaande waarden';
	});

	const showApplyButton = computed(() => {
		return riskLevel.value !== null;
	});

	const emit = defineEmits<{
		// eslint-disable-next-line no-unused-vars
		(e: 'apply-risk-level', riskLevel: Risk['level']): void;
	}>();

	const applyRiskLevel = () => {
		if (riskLevel.value !== null) {
			emit('apply-risk-level', riskLevel.value);
			detailsElement.value?.close();
		}
	};

	const getModelValue = (key: string) => {
		return formFields.value[key as keyof typeof formFields.value];
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const updateModelValue = (key: string, value: any) => {
		formFields.value[key as keyof typeof formFields.value] = value;
	};

	// Watch for changes in input values and reset riskLevel
	watch(formFields, () => riskLevel.value);
</script>

<style scoped>
	.fine-and-kinney {
		background: var(--color-grey-50);
		padding: 20px;
	}

	.form-wrapper {
		margin: 24px 0;
		display: grid;
		grid-gap: 12px;
		container-type: inline-size;
		container-name: form;
	}

	h4 {
		margin-bottom: var(--spacing-1)
	}

	:deep(select) {
		min-width: 75%;
	}
</style>
